import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, CalendarCheck2, Wallet, Trophy, FileText, IndianRupee } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/DataTable";

export default function MyPortalPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [children, setChildren] = useState([]);
  const [activeChild, setActiveChild] = useState(null);

  const loadStudent = async (studentId) => {
    const { data } = await api.get(`/portal/student/${studentId}`);
    setData(data);
  };

  useEffect(() => {
    (async () => {
      if (user?.role === "parent") {
        const { data } = await api.get("/portal/children");
        setChildren(data);
        if (data.length) {
          setActiveChild(data[0].id);
          await loadStudent(data[0].id);
        }
      } else {
        await loadStudent("me");
      }
    })();
    // eslint-disable-next-line
  }, [user]);

  if (!data) return <div className="text-[#00000099]">Loading portal…</div>;

  const s = data.student;
  const feeAmt = data.fees.reduce((sum, f) => sum + Number(f.amount||0), 0);
  const feePaid = data.fees.reduce((sum, f) => sum + Number(f.paid||0), 0);
  const feeDue = Math.max(0, feeAmt - feePaid);

  return (
    <div>
      <PageHeader
        title={user?.role === "parent" ? "Parent Portal" : "Student Portal"}
        subtitle={s.name + " · " + (s.course_name || "") + " · Roll: " + (s.roll_no || "—")}
        actions={user?.role === "parent" && children.length > 1 ? (
          <select
            data-testid="child-selector"
            value={activeChild || ""}
            onChange={async (e) => { setActiveChild(e.target.value); await loadStudent(e.target.value); }}
            className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white text-sm"
          >
            {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : null}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="linkedin-card p-4" data-testid="portal-attendance-card">
          <div className="kpi-label">Attendance</div>
          <div className="text-2xl font-semibold mt-1 text-[#000000E6]">{data.attendance_percent}%</div>
          <Progress value={data.attendance_percent} className="h-1.5 mt-2"/>
          <div className="text-xs text-[#00000099] mt-1">{data.attendance.filter(a=>a.status==='present').length} present · {data.attendance.filter(a=>a.status==='absent').length} absent</div>
        </div>
        <div className="linkedin-card p-4" data-testid="portal-fees-card">
          <div className="kpi-label">Fees</div>
          <div className="text-2xl font-semibold mt-1 text-[#000000E6]">₹{feeDue.toLocaleString('en-IN')}</div>
          <div className="text-xs text-[#00000099] mt-1">Outstanding of ₹{feeAmt.toLocaleString('en-IN')}</div>
        </div>
        <div className="linkedin-card p-4" data-testid="portal-tests-card">
          <div className="kpi-label">Tests Taken</div>
          <div className="text-2xl font-semibold mt-1 text-[#000000E6]">{data.test_results.length}</div>
          <div className="text-xs text-[#00000099] mt-1">Avg {data.test_results.length ? Math.round(data.test_results.reduce((s,r)=>s+r.percent,0)/data.test_results.length) : 0}%</div>
        </div>
        <div className="linkedin-card p-4" data-testid="portal-assignments-card">
          <div className="kpi-label">Submissions</div>
          <div className="text-2xl font-semibold mt-1 text-[#000000E6]">{data.submissions.length}</div>
          <div className="text-xs text-[#00000099] mt-1">{data.submissions.filter(x=>x.status==='evaluated').length} evaluated</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="linkedin-card p-5" data-testid="portal-recent-tests">
          <div className="flex items-center gap-2 mb-3"><Trophy size={16} className="text-[#0A66C2]"/><h3 className="font-semibold">Recent test scores</h3></div>
          <div className="divide-y divide-[#E0DFDC]">
            {data.test_results.length === 0 && <div className="text-sm text-[#00000099] py-4">No results yet</div>}
            {data.test_results.map(r => (
              <div key={r.id} className="py-2.5 flex justify-between items-center">
                <div>
                  <div className="text-sm font-semibold text-[#000000E6]">{r.test_name}</div>
                  <div className="text-xs text-[#00000099]">{r.correct_count}/{r.total_questions} correct · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-[#0A66C2]">{r.percent}%</div>
                  <div className="text-[11px] text-[#00000099]">{r.score}/{r.total}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="linkedin-card p-5" data-testid="portal-recent-attendance">
          <div className="flex items-center gap-2 mb-3"><CalendarCheck2 size={16} className="text-[#0A66C2]"/><h3 className="font-semibold">Recent attendance</h3></div>
          <div className="grid grid-cols-7 gap-1.5">
            {data.attendance.slice(0, 28).reverse().map(a => (
              <div key={a.id} data-testid={`att-day-${a.date}`}
                className={`aspect-square rounded flex flex-col items-center justify-center text-[10px] ${a.status === 'present' ? 'bg-[#D1E7DD] text-[#057642]' : 'bg-[#F8D7DA] text-[#CC1016]'}`}>
                <div className="font-semibold">{a.date?.slice(-2)}</div>
                <div className="uppercase">{a.status?.[0]}</div>
              </div>
            ))}
            {data.attendance.length === 0 && <div className="col-span-7 text-sm text-[#00000099] py-4 text-center">No attendance records</div>}
          </div>
        </div>

        <div className="linkedin-card p-5 lg:col-span-2" data-testid="portal-fees-detail">
          <div className="flex items-center gap-2 mb-3"><Wallet size={16} className="text-[#0A66C2]"/><h3 className="font-semibold">Fee status</h3></div>
          <div className="divide-y divide-[#E0DFDC]">
            {data.fees.length === 0 && <div className="text-sm text-[#00000099] py-4">No fee records</div>}
            {data.fees.map(f => (
              <div key={f.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{f.course_name || "Fee"}</div>
                  <div className="text-xs text-[#00000099]">Due {f.due_date}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[#000000E6]">₹{Number(f.amount).toLocaleString('en-IN')}</div>
                    <div className="text-[11px] text-[#00000099]">Paid ₹{Number(f.paid||0).toLocaleString('en-IN')}</div>
                  </div>
                  <StatusBadge status={f.status}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
